import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Skeleton,
  Alert,
  Button
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

interface WorkflowSummaryItem {
  id: number;
  status: string;
  current_step_index: number;
  job_title: string;
  department: string;
  template_name: string;
}

interface WorkflowSummaryProps {
  limit?: number;
  showViewAll?: boolean;
}

const WorkflowSummary: React.FC<WorkflowSummaryProps> = ({ 
  limit = 5, 
  showViewAll = true 
}) => {
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState<WorkflowSummaryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchWorkflows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchWorkflows = async () => {
    try {
      setLoading(true);
      const response = await api.get('/workflows/summary');
      setWorkflows(response.data.slice(0, limit));
      setError(null);
    } catch (err: any) {
      if (err.response?.status === 404) {
        // Workflows not enabled
        setError('Workflows feature is not enabled');
      } else {
        setError(err.response?.data?.error || 'Failed to load workflows');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string): "default" | "primary" | "success" | "warning" => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'primary';
      case 'not_started':
        return 'default';
      default:
        return 'warning';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon fontSize="small" />;
      case 'in_progress':
        return <TrendingUpIcon fontSize="small" />;
      case 'not_started':
        return <ScheduleIcon fontSize="small" />;
      default:
        return <WarningIcon fontSize="small" />;
    }
  };

  if (!process.env.REACT_APP_ENABLE_WORKFLOWS || process.env.REACT_APP_ENABLE_WORKFLOWS !== 'true') {
    return null;
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Active Hiring Workflows
          </Typography>
          {showViewAll && workflows.length > 0 && (
            <Button size="small" onClick={() => navigate('/workflows')}>
              View All
            </Button>
          )}
        </Box>

        {loading ? (
          <Box>
            {[...Array(3)].map((_, i) => (
              <Box key={i} sx={{ mb: 2 }}>
                <Skeleton variant="text" width="80%" height={24} />
                <Skeleton variant="text" width="60%" height={20} />
                <Skeleton variant="rectangular" height={4} sx={{ mt: 1 }} />
              </Box>
            ))}
          </Box>
        ) : error ? (
          <Alert severity="info">
            {error}
          </Alert>
        ) : workflows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
            No active workflows found
          </Typography>
        ) : (
          <List disablePadding>
            {workflows.map((workflow, index) => (
              <ListItem
                key={workflow.id}
                divider={index < workflows.length - 1}
                sx={{ px: 0, cursor: 'pointer' }}
                onClick={() => navigate(`/jobs/${workflow.id}`)}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2">
                        {workflow.job_title}
                      </Typography>
                      <Chip
                        label={workflow.department}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {workflow.template_name}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={(workflow.current_step_index / 10) * 100} // Approximate based on typical workflow length
                        sx={{ height: 4, borderRadius: 2, mt: 0.5 }}
                      />
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Chip
                    icon={getStatusIcon(workflow.status)}
                    label={workflow.status.replace('_', ' ')}
                    size="small"
                    color={getStatusColor(workflow.status)}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default WorkflowSummary;